
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { EmptyComponent, SelectComponent } from 'nebula-ui-kit';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderAction,
  PageHeaderComponent,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import { finalize, timeout } from 'rxjs';
import { IngestProjectService } from '../../../../../data-access/src/lib/ingest/ingest-project.service';
import { IngestProject } from '../../../../../domain/src/lib/ingest/ingest-project.model';
import { INGEST_MODULE_ENV } from '@nebula/shared-vault/data-access';
import { MOCK_INGEST_PROJECTS } from '../ingest-ui-mock.data';
import { ingestUiStore } from '../../../../../store/src/lib/ingest-ui.store';

@Component({
  selector: 'app-ingest-upload-hub',
  standalone: true,
  imports: [
    PageHeaderComponent,
    EmptyComponent,
    FieldLabelComponent,
    SelectComponent,
    GridFormComponent,
    GridFormItemComponent,
    VaultPageAlertComponent,
],
  templateUrl: './ingest-upload-hub.component.html',
  styleUrls: ['./ingest-upload-hub.component.scss'],
})
export class IngestUploadHubComponent implements OnInit {
  private readonly ingestProjectService = inject(IngestProjectService);
  private readonly ingestEnv = inject(INGEST_MODULE_ENV);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly uiStore = inject(ingestUiStore);

  readonly projects = signal<IngestProject[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);

  readonly activeProjects = computed(() => this.projects());

  readonly projectOptions = computed(() =>
    this.activeProjects().map((p) => ({
      label: `${p.name} (${p.folder_path})`,
      value: p.id,
    })),
  );

  selectedProjectId = model<number | undefined>(undefined);

  readonly headerActions = computed<PageHeaderAction[]>(() => {
    const selected = this.selectedActiveProject();
    const disabled = !selected;
    return [
      {
        id: 'upload',
        label: 'Ir a carga',
        icon: 'Upload',
        variant: 'primary',
        disabled,
      },
      {
        id: 'detail',
        label: 'Ver detalle',
        icon: 'Eye',
        variant: 'secondary',
        disabled,
      },
    ];
  });

  private readonly selectedActiveProject = computed(() => {
    const id = this.selectedProjectId();
    if (!id) return null;
    return this.activeProjects().find((project) => project.id === id) ?? null;
  });

  ngOnInit(): void {
    const saved = this.uiStore.uploadHubSelectedProjectId();
    if (saved && Number.isFinite(saved)) {
      this.selectedProjectId.set(saved);
    }
    this.error.set(null);
    this.info.set(null);
    this.loading.set(true);
    if (this.ingestEnv.previewMode) {
      this.applyLoadedProjects(
        MOCK_INGEST_PROJECTS.filter((project) => project.is_active).map((p) => ({ ...p })),
      );
      this.loading.set(false);
      return;
    }
    this.ingestProjectService
      .listForIngest()
      .pipe(
        timeout(45000),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (list) => {
          this.applyLoadedProjects(list ?? []);
        },
        error: () => {
          this.error.set('No se pudieron cargar los expedientes.');
          this.projects.set([]);
        },
      });
  }

  private applyLoadedProjects(list: IngestProject[]): void {
    this.projects.set(list);
    const selectedId = this.selectedProjectId();
    if (selectedId && !list.some((project) => project.id === selectedId)) {
      this.selectedProjectId.set(undefined);
      this.uiStore.setUploadHubSelectedProjectId(null);
    }
    if (list.length === 0) {
      this.info.set(
        'No hay expedientes activos disponibles para ingesta. Active un expediente desde la lista de expedientes.',
      );
      return;
    }
    this.info.set(null);
  }

  goToUpload(): void {
    const project = this.selectedActiveProject();
    if (!project) return;
    void this.router.navigate(['projects', project.id, 'upload'], {
      relativeTo: this.route.parent,
    });
  }

  goToProjectDetail(): void {
    const id = this.selectedProjectId();
    if (!id) return;
    void this.router.navigate(['projects', id], { relativeTo: this.route.parent });
  }

  onHeaderAction(actionId: string): void {
    if (actionId === 'upload') {
      this.goToUpload();
    } else if (actionId === 'detail') {
      this.goToProjectDetail();
    }
  }

  onSelectedProjectIdChange(value: number | undefined): void {
    const project = value
      ? this.activeProjects().find((item) => item.id === value)
      : undefined;
    const nextId = project?.id;
    this.selectedProjectId.set(nextId);
    this.uiStore.setUploadHubSelectedProjectId(nextId ?? null);
  }
}
